import { Button, Divider, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import React, { PropsWithChildren } from "react";
import { DownloadLink } from "../DownloadLink";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

export type DataCardListsValue = {
  selected: Array<string>;
  unselected: Array<string>;
}

export type DataCardValueWithDetail = {
  value: string;
  detail: string;
}

export type DataCardDownload = {
  fileName: string;
  getBlob: () => Promise<Blob>;
}

export type DataCardItemType = "DEFAULT" | "MULTILINE" | "LIST" | "LIST_WITH_UNSELECTED" | "VALUE_WITH_DETAIL" | "DOWNLOAD";
export type DataCardItemValue = string | Array<string> | DataCardListsValue | DataCardValueWithDetail | DataCardDownload;

type DataCardItem =
  | DataCardDefaultItem
  | DataCardMultilineItem
  | DataCardListItem
  | DataCardListWithUnselectedItem
  | DataCardValueWithDetailItem
  | DataCardDownloadItem;

export type DataCardDefaultItem = {
  itemType: "DEFAULT";
  key: string;
  value: string;
}

export type DataCardMultilineItem = {
  itemType: "MULTILINE";
  key: string;
  value: string;
}

export type DataCardListItem = {
  itemType: "LIST";
  key: string;
  value: Array<string>;
}

export type DataCardListWithUnselectedItem = {
  itemType: "LIST_WITH_UNSELECTED";
  key: string;
  value: DataCardListsValue;
}

export type DataCardValueWithDetailItem = {
  itemType: "VALUE_WITH_DETAIL";
  key: string;
  value: DataCardValueWithDetail;
}

export type DataCardDownloadItem = {
  itemType: "DOWNLOAD";
  key: string;
  value: DataCardDownload;
}

export type DataCardProps = {
  showHeading: boolean;
  heading: string | null;
  items: Array<DataCardItem>;
  hasAction: boolean;
  actionDescription: string | null;
  actionButtonText: string | null;
  actionFunction: Function | null;
  headingComponent: "h1" | "h2" | "h3" | "h4" | "h5" | "h6",
  optionalComponent?: React.ReactNode;
}

const renderDefaultItem = (item: DataCardDefaultItem) => (
  <>
    <Grid item xs={12} sm={3}>
      <Typography variant="body1" data-testid="data-card-key"><strong>{item.key}</strong></Typography>
    </Grid>
    <Grid item xs={12} sm={9}>
      <Typography variant="body1" data-testid="data-card-value">{item.value}</Typography>
    </Grid>
  </>
);

// todo check this is even required
const renderMultilineItem = (item: DataCardMultilineItem) => (
  <>
    <Grid item xs={12} sm={3}>
      <Typography variant="body1" data-testid="data-card-key"><strong>{item.key}</strong></Typography>
    </Grid>
    <Grid item xs={12} sm={9}>
      <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }} data-testid="data-card-value">{item.value}</Typography>
    </Grid>
  </>
);

const renderListItem = (item: DataCardListItem) => (
  <>
    <Grid item xs={12} sm={3}>
      <Typography variant="body1" data-testid="data-card-key"><strong>{item.key}</strong></Typography>
    </Grid>
    <Grid item xs={12} sm={9}>
      <ul data-testid="data-card-value">
        {item.value.map(x => (
          <li key={x}><Typography variant="body1">{x}</Typography></li>
        ))}
      </ul>
    </Grid>
  </>
);

const renderListWithUnselectedItem = (item: DataCardListWithUnselectedItem) => (
  <>
    <Grid item xs={12} sm={3}>
      <Typography variant="body1" data-testid="data-card-key"><strong>{item.key}</strong></Typography>
    </Grid>
    <Grid item xs={12} sm={9}>
      <Typography variant="body1"><strong>Selected:</strong></Typography>
      <ul data-testid="data-card-value-selected">
        {item.value.selected.map(x => (
          <li key={x}><Typography variant="body1">{x}</Typography></li>
        ))}
      </ul>
      <Typography variant="body1"><strong>Not selected:</strong></Typography>
      <ul data-testid="data-card-value-unselected">
        {item.value.unselected.map(x => (
          <li key={x}><Typography variant="body1">{x}</Typography></li>
        ))}
      </ul>
    </Grid>
  </>
);

const renderValueWithDetailItem = (item: DataCardValueWithDetailItem) => (
  <>
    <Grid item xs={12} sm={3}>
      <Typography variant="body1" data-testid="data-card-key"><strong>{item.key}</strong></Typography>
    </Grid>
    <Grid item xs={12} sm={9} container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="body1" data-testid="data-card-value">{item.value.value}</Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }} data-testid="data-card-value-detail">{item.value.detail}</Typography>
      </Grid>
    </Grid>
  </>
)

const renderDownload = (item: DataCardDownloadItem) => (
  <>
    <Grid item xs={12} sm={3}>
      <Typography variant="body1" data-testid="data-card-key"><strong>{item.key}</strong></Typography>
    </Grid>
    <Grid item xs={12} sm={9} data-testid="data-card-value">
      <DownloadLink fileName={item.value.fileName} getBlob={item.value.getBlob} />
    </Grid>
  </>
)

const renderItem = (item: DataCardItem) => {
  switch (item.itemType) {
    case "DEFAULT":
      return renderDefaultItem(item);
    case "MULTILINE":
      return renderMultilineItem(item);
    case "LIST":
      return renderListItem(item);
    case "LIST_WITH_UNSELECTED":
      return renderListWithUnselectedItem(item);
    case "VALUE_WITH_DETAIL":
      return renderValueWithDetailItem(item);
    case "DOWNLOAD":
      return renderDownload(item);
    default:
      throw new Error("Unsupported data card item type");
  }
}

export const DataCard: React.FC<DataCardProps> = ({
  showHeading = false,
  heading = null,
  headingComponent = "h3",
  items = [],
  hasAction = false,
  actionButtonText = null,
  actionDescription = null,
  actionFunction = null,
  optionalComponent
}) => {
  const styles = useStyles();

  return (
    <Paper className={styles.formSection}>
      <Grid container spacing={1}>
        {showHeading &&
          <>
            <Grid item xs={12}>
              <Typography variant="h5" component={headingComponent} data-testid="data-card-heading">{heading}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </>
        }
        {items.map(x => (
          <React.Fragment key={x.key}>
            {renderItem(x)}
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </React.Fragment>
        ))}
        {hasAction && (
          <Grid container item xs={12} justifyContent="space-between" alignItems="center">
            <Grid item>
              <Typography variant="body1" data-testid="data-card-action-desc">
                {actionDescription}
              </Typography>
            </Grid>
            <Grid item>
              <Button onClick={(_) => !!actionFunction ? actionFunction() : {}} data-testid="data-card-action-btn">
                {actionButtonText}
              </Button>
            </Grid>
          </Grid>
        )}
        {optionalComponent && optionalComponent}
      </Grid>
    </Paper>
  )
}